import i18next from 'i18next';
import _ from 'lodash';
import { INSIGHT_TELEMETRY_BOARD, trackInsight } from '@/actionBoards/actionBoard.telemetry';
import {
  displayNewOrExisting,
  fetchAnnotations,
  findDefaultJournalsOnServer,
  mergeAnnotationsIntoStore,
  save,
} from '@/annotation/annotation.actions';
import { findExclusiveEntry, findJournalEntries } from '@/annotation/annotation.store';
import {
  sqAnnotationStore,
  sqDurationStore,
  sqLicenseManagementStore,
  sqReportStore,
  sqStateSynchronizer,
  sqWorkbenchStore,
  sqWorkbookStore,
  sqWorkstepsStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE, PUSH_WORKSTEP_IMMEDIATE } from '@/core/flux.service';
import { loadDashboard } from '@/dashboard/dashboard.actions';
import { splitDuration } from '@/datetime/dateTime.utilities';
import { loadDisplay } from '@/displays/displays.actions';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import {
  setUnsubscribeFromLockingChannel,
  setUnsubscribeFromReportUpdatesChannel,
  setUnsubscribeFromWorkbook,
  setUnsubscribeFromWorkstepChannel,
} from '@/main/cleanup.utilities';
import { setAutoFocusTableDefinitionTab, setStatisticsSummaryTabVisible } from '@/main/layout.actions';
import {
  getCurrentPath,
  getFromBuilder,
  getNoWorksheetLink,
  getViewWorksheetLink,
  getWorksheetPath,
  goTo,
  goToWithReload,
  navigateToCorrectErrorPage,
  navigateToUnauthorizedPage,
  setFromBuilder,
  wasWorksheet,
} from '@/main/routing.utilities';
import { debouncedOnReport, loadReport, setIsOffline } from '@/reportEditor/report.actions';
import { setAnalysisViewType } from '@/reportEditor/reportContent.actions';
import { swapAsset, swapScalingTableRowFromLoadOptions } from '@/search/searchResult.utilities.service';
import { canWriteItem, isTrustedUser } from '@/services/authorization.service';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { addOnToolsEnabled, isVantageEnabled } from '@/services/systemConfiguration.utilities';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { setAddOnTools as setAddOnToolsSqInvestigation } from '@/toolSelection/investigate.actions';
import { doTrack } from '@/track/track.service';
import { setParent } from '@/treemap/treemap.actions';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { autoUpdate, displayRange, investigateRange } from '@/trendData/duration.actions';
import { addTrendItem, setHideUncertainty, setTrendSummary, swapAssets } from '@/trendData/trend.actions';
import { AUTO_UPDATE, SummarySliderValues } from '@/trendData/trendData.constants';
import { clearOutstandingDebouncedAsyncFunctions } from '@/utilities/asyncDebounce';
import { getDependencies } from '@/utilities/formula.utilities';
import { getWorkbook as getWorkbookWithWorksheets, updateOpenedAt } from '@/utilities/homescreen.utilities';
import { isHttpAbortError } from '@/utilities/http.utilities';
import { addAssetsProperty } from '@/utilities/httpHelpers.utilities';
import { logError, logWarn } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { notifyCapture, notifyError, notifyLoading } from '@/utilities/screenshot.utilities';
import { subscribe, subscribeToLockingChannel } from '@/utilities/socket.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { isPresentationWorkbookMode } from '@/utilities/workbookMode.utilities';
import { setCurrentUser } from '@/workbench/workbench.actions';
import {
  loadWorkbook,
  resetViewingState,
  setLockedChangeMade,
  setViewers,
  setWorkbookDisplayMode,
  setWorksheetProperty,
} from '@/workbook/workbook.actions';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { getWorkbook, parseSavedWorkbookState } from '@/workbook/workbook.utilities';
import { setTimezone } from '@/worksheet/worksheet.actions';
import { createWorksheet, getWorksheet, getWorksheets } from '@/worksheets/worksheets.utilities';
import { currentWorkstepAction, getWorkstepAction } from '@/worksteps/worksteps.actions';
import { WORKSHEET_WITH_NO_WORKSTEPS } from '@/worksteps/worksteps.constant';
import { workstepFromResponse, workstepOrigin } from '@/worksteps/worksteps.utilities';
import { sqTreesApi } from '@seeqdev/api/endpoint/TreesApi';
import { SummaryTypeEnum } from '@seeqdev/api/model/ContentInputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { Annotation } from '@/annotation/annotation.types';
import type { SearchModes } from '@/search/search.constants';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { LockedMetadataIF } from '@/workbook/workbook.constants';
import type {
  LoadOptions,
  NormalizedWorkbookOutput,
  StoreWorksheet,
  WorkbookApprovalFlowAttachment,
  WorkbookApprovalFlowAttachmentData,
  WorkbookApprovalFlowData,
  WorkbookQueryResult,
  WorkbookQueryWorkbook,
} from '@/worksheet/worksheet.types';
import type { WorkstepOutput } from '@/worksteps/worksteps.utilities';
import type { GqlApprovalFlowSectionFragment } from '@seeqdev/api/graphql/ApprovalFlowSection';
import type { GqlGetWorkbookQuery } from '@seeqdev/api/graphql/GetWorkbook';

export const exposedForTesting = {
  beforeFetchUpdateWorkstepState,
  loadWorkstep,
  get saveDefaultAnnotation() {
    return saveDefaultAnnotation;
  },
};

type NormalizedWorksheetLoadData = Awaited<ReturnType<typeof normalizeGqlOutput>>;

// These stores hold information on different options for viewing a worksheet such as dimming, or column names to
// display in details panel, they do not hold any specific data or content for a worksheet, this list is used to apply
// saved worksheet preferences from a saved template workstep
export const STORES_REHYDRATED_BY_WORKSTEP_TEMPLATE = {
  sqScatterPlotStore: [
    'connect',
    'isMarkerSizeCustom',
    'markerSize',
    'numXBins',
    'numYBins',
    'plotMode',
    'plotView',
    'showTooltips',
  ],
  sqTableBuilderStore: ['isTableStriped', 'mode'],
  sqTrendStore: [
    'capsulePreview',
    'customizationMode',
    'dataSummary',
    'dimDataOutsideCapsules',
    'enabledColumns',
    'hideUnselectedItems',
    'hideUncertainty',
    'isCompareMode',
    'labelDisplayConfiguration',
    'showCapsuleLaneLabels',
    'showGridlines',
    'trendColorSettings',
    'view',
  ],
};

export const goToPathWithWorksheetId = async ({ workbookDisplay, workbookId, currentFolderId }: LoadOptions) => {
  try {
    const worksheet =
      (await getWorksheets(workbookId))[0] ?? (await findWorksheetForWorkbookWithoutWorksheets(workbookId));
    if (!worksheet) {
      goTo(getNoWorksheetLink(), true);
      return;
    }

    const worksheetLink = getWorksheetPath(workbookId, worksheet.worksheetId!, currentFolderId, workbookDisplay);

    // location: 'replace' ensures the browser history gets overwritten and prevents CRAB-15738
    goTo(worksheetLink, true);
  } catch (err) {
    navigateToCorrectErrorPage(err, 'ROUTE_NO_WORKBOOK');
  }
};

/**
 * Finds the worksheet to open for a workbook that has no live worksheets. Archiving a workbook archives its worksheets
 * too, so an archived workbook opens on its first archived worksheet, the same as opening it from the trash. A new
 * worksheet is only created when the workbook can be edited: a worksheet created on an archived or locked workbook can
 * never receive a workstep, so it can never load (CRAB-63893).
 */
async function findWorksheetForWorkbookWithoutWorksheets(workbookId: string): Promise<StoreWorksheet | undefined> {
  const workbook = await getWorkbookWithWorksheets(workbookId);
  const worksheet = workbook.worksheets?.[0];
  if (worksheet) {
    return worksheet;
  }

  return !workbook.isArchived && canWriteItem(workbook) ? createWorksheet(workbookId, '1') : undefined;
}

const subscribeToSubscriptionChannel = (loadOptions: LoadOptions) => {
  if (!headlessRenderMode()) {
    // Subscribe to this workbook's subscription channel to get updates about who is viewing this workbook.
    // This is a low priority subscription, so we should not wait for it to complete nor should we fail if
    // it doesn't succeed.
    const unsubscribeMethod = subscribe({
      channelId: [SeeqNames.Channels.WorkbookChannel, loadOptions.workbookId, 'subscriptions'],
      onMessage: setViewers,
      subscriberParameters: {
        worksheetId: loadOptions.worksheetId,
        worksheetDisplay: loadOptions.workbookDisplay,
      },
    });
    setUnsubscribeFromLockingChannel(subscribeToLockingChannel(loadOptions));
    setUnsubscribeFromWorkbook([
      () => {
        unsubscribeMethod();
        // The workbook store had an out-of-date list of viewers when transitioning workbooks. This clears the
        // viewers so that the user doesn't see off-by-one user counts on the worksheet they're coming from.
        flux.dispatch('WORKBOOK_SET_VIEWERS', []);
      },
      subscribe({
        channelId: [SeeqNames.Channels.ThumbnailChannel, loadOptions.workbookId],
        onMessage: ({ worksheetId }) => setWorksheetProperty(worksheetId, 'usingLatestThumbnail', true),
      }),
    ]);

    // Subscribe to this worksheet's workstep channel to follow worksteps.
    setUnsubscribeFromWorkstepChannel(
      subscribe({
        channelId: [SeeqNames.Channels.WorkstepChannel, loadOptions.worksheetId],
        onMessage: (data) => sqStateSynchronizer.onWorkstep(data),
      }),
    );

    // Subscribe to this reports update channel to follow report updates.
    if (sqWorkbookStore.isTopic) {
      setUnsubscribeFromReportUpdatesChannel(
        subscribe({
          channelId: [SeeqNames.Channels.ReportUpdateChannel, loadOptions.worksheetId],
          onMessage: debouncedOnReport,
          onClose: () => setIsOffline(true),
          onSubscribe: () => setIsOffline(false),
        }),
      );
    }
  }
};

export const trackOpenedDocument = (loadOptions: LoadOptions) => {
  const displayMode = _.last(loadOptions.workbookDisplay?.split('.'));
  if (sqWorkbookStore.isTopic) {
    const liveDoc = sqReportStore.hasLiveContent;
    doTrack('Topic', 'Opened', {
      mode: displayMode,
      liveDoc,
      workbookId: loadOptions.workbookId,
      worksheetId: loadOptions.worksheetId,
      darkMode: sqWorkbenchStore.darkMode,
    });
  } else if (sqWorkbookStore.isVantage) {
    const action = `Opened${
      (!isVantageEnabled() && ': Not Enabled') ||
      (!hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Vantage) && ': Not Licensed') ||
      ''
    }`;
    doTrack('Vantage', action);
  } else if (sqWorkbookStore.isActionBoard) {
    trackInsight({
      category: INSIGHT_TELEMETRY_BOARD,
      action: 'Opened',
      mode: displayMode,
      workbookId: loadOptions.workbookId,
      worksheetId: loadOptions.worksheetId,
      darkMode: sqWorkbenchStore.darkMode,
    });
  } else {
    doTrack('Analysis', 'Opened', {
      mode: displayMode,
      workbookId: loadOptions.workbookId,
      worksheetId: loadOptions.worksheetId,
      darkMode: sqWorkbenchStore.darkMode,
      isLocked: sqWorkbookStore.isLocked,
    });
  }
};

const loadReportInfo = async (loadOptions: LoadOptions) => {
  // Try to get the worksheet from the workbook store. It should be there unless it has been trashed.
  const worksheet = _.find(sqWorkbookStore.worksheets, ['worksheetId', loadOptions.worksheetId]);

  // The worksheet can be undefined if a user accesses the topic document using a shared URL after the
  // document has been trashed from the topic. Normally we can avoid an extra call to the backend to get
  // the worksheet but in the case where is has been trashed we need to make it because we want the
  // trashed document to still be viewable via the URL.

  const { reportId, isDashboard } = _.isNil(worksheet)
    ? await getWorksheet(loadOptions.workbookId, loadOptions.worksheetId, true)
    : worksheet;
  isDashboard ? await loadDashboard(reportId!) : await loadReport(reportId!);
  setWorksheetProperty(loadOptions.worksheetId, 'reportId', reportId!);
};

const inFlightDefaultAnnotations = new Map<string, Promise<unknown>>();

/** Creates the default ("Unnamed") journal for a worksheet (or workbook, for Vantage) opened in EDIT mode if none exists. */
const saveDefaultAnnotation = async (loadOptions: LoadOptions) => {
  const target = sqWorkbookStore.isVantage ? loadOptions.workbookId : loadOptions.worksheetId;
  const inFlight = inFlightDefaultAnnotations.get(target);
  if (inFlight) {
    await inFlight;
    return;
  }

  if (headlessRenderMode() || loadOptions.workbookDisplay !== WORKBOOK_DISPLAY.EDIT) {
    return;
  }

  const { annotations } = sqAnnotationStore.getState();
  const storeHasAnnotations = (
    sqWorkbookStore.isVantage
      ? findExclusiveEntry(annotations, loadOptions.workbookId)
      : findJournalEntries(annotations, loadOptions.worksheetId)
  ).length;
  if (storeHasAnnotations) {
    return;
  }

  const worksheetId = sqWorkbookStore.isVantage ? null : loadOptions.worksheetId;
  const createPromise = (async () => {
    let serverJournals: Annotation[] | null;
    try {
      serverJournals = await findDefaultJournalsOnServer(loadOptions.workbookId, worksheetId);
    } catch (error) {
      logWarn(formatMessage`Could not check for an existing default journal: ${error}`, 'worksheet');
      return;
    }
    if (serverJournals === null || serverJournals.length) {
      mergeAnnotationsIntoStore(serverJournals ?? []);
      return;
    }
    await save({
      workbookId: loadOptions.workbookId,
      worksheetId,
      name: i18next.t('UNNAMED'),
    });
  })();
  inFlightDefaultAnnotations.set(target, createPromise);
  try {
    await createPromise;
  } finally {
    inFlightDefaultAnnotations.delete(target);
  }
};

function toServerErrorMessage(serverError: { field: string; message: string; path: string[] }) {
  return `${serverError.field}: ${serverError.message}`;
}

function isAccessDeniedServerError(serverError: { message: string }) {
  return serverError.message.includes('does not have access');
}

function getWorkbookLoadServerError(workbookData: WorkbookQueryResult) {
  return workbookData.workbook.serverErrors.find(
    (serverError) => !['worksheet', 'selectedWorksheet'].includes(serverError.field),
  );
}

function getWorksheetLoadServerError(workbookData: WorkbookQueryResult) {
  const workbookWorksheetError = workbookData.workbook.serverErrors.find((serverError) =>
    ['worksheet', 'selectedWorksheet'].includes(serverError.field),
  );

  return workbookWorksheetError ?? workbookData.workbook.selectedWorksheet?.serverErrors?.[0];
}

function normalizeGqlWorksheet(
  worksheet:
    | NonNullable<GqlGetWorkbookQuery['workbook']['selectedWorksheet']>
    | NonNullable<GqlGetWorkbookQuery['workbook']['worksheets']>[number],
) {
  return {
    worksheetId: worksheet.id,
    boardId: worksheet.boardId,
    name: worksheet.name,
    createdAt: worksheet.createdAt,
    updatedAt: worksheet.updatedAt,
    isArchived: worksheet.isArchived,
    isDashboard: worksheet.isDashboard,
    duplicationErrorDocument: worksheet.duplicationErrorDocument ?? undefined,
    reportId: worksheet.report?.id ?? undefined,
    workstep: worksheet.currentWorkstep?.id,
  };
}

function normalizeGqlOwner(owner: GqlGetWorkbookQuery['workbook']['owner']) {
  return owner
    ? {
        id: owner.id!,
        name: owner.name as string,
        email: owner.email ?? undefined,
        username: owner.username ?? undefined,
        type: owner.type as string,
      }
    : undefined;
}

function normalizeUnauthorizedOwnerInfo(owner: GqlGetWorkbookQuery['workbook']['owner']) {
  return owner
    ? {
        name: owner.name ?? '',
        email: owner.email ?? '',
      }
    : undefined;
}

function normalizeGqlAncestor(ancestor: GqlGetWorkbookQuery['workbook']['ancestors'][number]) {
  return {
    id: ancestor.id!,
    name: ancestor.name as string,
    type: ancestor.type as string,
    translationKey: ancestor.translationKey ?? undefined,
  };
}

function normalizeGqlApprovalFlowStep(step: GqlApprovalFlowSectionFragment['approvalFlows'][number]['steps'][number]) {
  return {
    id: step.id,
    approvalFlowId: step.approvalFlowId,
    name: step.name,
    lockRequired: step.lockRequired,
    position: step.position,
    archived: step.archived,
    createdAt: step.createdAt,
    updatedAt: step.updatedAt ?? undefined,
    permissions: step?.permissions,
    status: step.status,
  };
}

function normalizeGqlApprovalFlow(flow: GqlApprovalFlowSectionFragment['approvalFlows'][number]) {
  return {
    id: flow.id,
    name: flow.name,
    archived: flow.archived,
    createdAt: flow.createdAt,
    updatedAt: flow.updatedAt ?? undefined,
    steps: flow.steps.map(normalizeGqlApprovalFlowStep),
  };
}

function normalizeGqlApprovalFlowAssignment(
  assignment: GqlApprovalFlowSectionFragment['approvalFlowAssignments'][number],
) {
  return {
    id: assignment.id,
    approvalFlowId: assignment.approvalFlowId,
    stepId: assignment.stepId,
    itemId: assignment.itemId,
    approvedBy: assignment.approvedBy,
    approvedAt: assignment.approvedAt,
  };
}

export function normalizeGqlApprovalFlowData(workbook: WorkbookQueryWorkbook | undefined): WorkbookApprovalFlowData {
  return {
    approvalFlows: (workbook?.approvalFlows || [])?.map(normalizeGqlApprovalFlow),
    approvalFlowAssignments: (workbook?.approvalFlowAssignments || [])?.map(normalizeGqlApprovalFlowAssignment),
    attachments: (workbook?.attachments || [])?.map(normalizeGqlAttachment),
  };
}

export function normalizeGqlAttachmentData(
  workbook: WorkbookQueryWorkbook | undefined,
): WorkbookApprovalFlowAttachmentData {
  return {
    attachments: (workbook?.attachments || [])?.map(normalizeGqlAttachment),
  };
}

export function normalizeGqlAttachment(
  attachment: GqlApprovalFlowSectionFragment['attachments'][number],
): WorkbookApprovalFlowAttachment {
  return {
    id: attachment.id,
    itemId: attachment.itemId,
    approvalFlowId: attachment.approvalFlowId,
    assignedBy: attachment.assignedBy,
    createdAt: attachment.createdAt,
    archived: attachment.archived,
  };
}

export async function normalizeGqlOutput(workbookData: WorkbookQueryResult): Promise<NormalizedWorkbookOutput> {
  const { workbook } = workbookData;
  const selectedWorksheet = workbook.selectedWorksheet;
  const worksheets = (workbook.worksheets ?? []).map(normalizeGqlWorksheet);

  if (selectedWorksheet && !_.some(worksheets, ['worksheetId', selectedWorksheet.id])) {
    worksheets.push(normalizeGqlWorksheet(selectedWorksheet));
  }

  const workstep = selectedWorksheet?.requestedWorkstep ?? selectedWorksheet?.currentWorkstep;
  const selectedWorkstep = workstep ? await workstepFromResponse(workstep) : undefined;
  const savedWorkbookState = workbook.workbookState ? parseSavedWorkbookState(workbook.workbookState) : undefined;
  return {
    workbook: {
      id: workbook.id,
      workbookId: workbook.id,
      name: workbook.name,
      description: workbook.description ?? '',
      owner: normalizeGqlOwner(workbook.owner),
      createdAt: workbook.createdAt,
      updatedAt: workbook.updatedAt,
      type: workbook.type,
      worksheets,
      isArchived: workbook.isArchived,
      parentFolderId: workbook.parentFolderId ?? '',
      ancestors: workbook.ancestors.map(normalizeGqlAncestor),
      effectivePermissions: workbook.effectivePermissions,
      isLocked: workbook.isLocked,
      lockedMetadata: workbook.lockedMetadata ? parseLockedMetadata(workbook.lockedMetadata) : undefined,
      ...normalizeGqlApprovalFlowData(workbook),
      ...normalizeGqlAttachmentData(workbook),
    },
    workbookState: savedWorkbookState?.state,
    workbookStateOrigin: savedWorkbookState?.origin,
    workstep: selectedWorkstep,
  };
}

function parseLockedMetadata(lockedMetadataJson: string): LockedMetadataIF | undefined {
  try {
    return JSON.parse(lockedMetadataJson) as LockedMetadataIF;
  } catch {
    return undefined;
  }
}

function shouldLoadWorkbook(loadOptions: LoadOptions) {
  return !!(
    loadOptions.workbookId !== _.get(sqWorkbenchStore.previousStateParams, 'workbookId') ||
    !wasWorksheet() ||
    loadOptions.isDefaultWorksheet
  );
}

async function loadVantageWorksheetData(loadOptions: LoadOptions, workbookLoadNeeded: boolean) {
  const workbook = workbookLoadNeeded
    ? await loadWorkbook(loadOptions.workbookId, loadOptions.workbookDisplay, loadOptions.onVantageWorkbookLoaded)
    : sqWorkbookStore.workbook;

  const savedWorkbookState = await getWorkbook(loadOptions.workbookId);
  return {
    workbook,
    workbookState: savedWorkbookState?.state,
    workbookStateOrigin: savedWorkbookState?.origin,
    workstep: undefined,
    shouldSetAddOnTools:
      addOnToolsEnabled() &&
      isTrustedUser() &&
      workbookLoadNeeded &&
      !sqWorkbookStore.isTopic &&
      !isPresentationWorkbookMode(loadOptions.workbookDisplay),
  };
}

function loadGraphqlWorksheetData(
  loadOptions: LoadOptions,
  normalizedWorkbook: NormalizedWorksheetLoadData,
  workbookLoadNeeded: boolean,
) {
  let workbook: any;
  if (workbookLoadNeeded) {
    workbook = {
      ...normalizedWorkbook.workbook,
      workbookDisplay: loadOptions.workbookDisplay ?? WORKBOOK_DISPLAY.VIEW,
    };
    flux.dispatch('WORKBOOK_SET', workbook, PUSH_IGNORE);
  } else {
    workbook = sqWorkbookStore.workbook;
    flux.dispatch(
      'WORKBOOK_SET_APPROVAL_FLOW',
      {
        approvalFlows: normalizedWorkbook.workbook.approvalFlows,
        approvalFlowAssignments: normalizedWorkbook.workbook.approvalFlowAssignments,
        attachments: normalizedWorkbook.workbook.attachments,
      },
      PUSH_IGNORE,
    );
  }

  if (!sqWorkbookStore.getWorksheet(loadOptions.worksheetId)) {
    const selectedWorksheet = normalizedWorkbook.workbook.worksheets.find(
      (worksheet) => worksheet.worksheetId === loadOptions.worksheetId,
    );
    if (selectedWorksheet) {
      flux.dispatch('WORKBOOK_SET_ARCHIVED_WORKSHEET', selectedWorksheet, PUSH_IGNORE);
    }
  }

  return {
    workbook,
    workbookState: normalizedWorkbook.workbookState,
    workbookStateOrigin: normalizedWorkbook.workbookStateOrigin,
    workstep: normalizedWorkbook.workstep,
    shouldSetAddOnTools:
      addOnToolsEnabled() &&
      isTrustedUser() &&
      workbookLoadNeeded &&
      !sqWorkbookStore.isTopic &&
      !isPresentationWorkbookMode(loadOptions.workbookDisplay),
  };
}

/**
 * Loads a worksheet using a specified set of parameters. The actual contents of Workbook, Worksheet, and Workstep
 * are preloaded via [workbookData]. We do fallback to fetching in some Vantage related cases.
 */
export async function loadWorksheet(loadOptions: LoadOptions, workbookData: WorkbookQueryResult) {
  clearOutstandingDebouncedAsyncFunctions();

  try {
    const workbookLoadServerError = getWorkbookLoadServerError(workbookData);
    if (workbookLoadServerError) {
      if (isAccessDeniedServerError(workbookLoadServerError)) {
        navigateToUnauthorizedPage(normalizeUnauthorizedOwnerInfo(workbookData.workbook.owner));
      } else {
        navigateToCorrectErrorPage(toServerErrorMessage(workbookLoadServerError), 'ROUTE_NO_WORKBOOK');
      }
      return;
    }

    const worksheetLoadServerError = getWorksheetLoadServerError(workbookData);
    if (worksheetLoadServerError) {
      navigateToCorrectErrorPage(toServerErrorMessage(worksheetLoadServerError), 'ROUTE_NO_WORKSHEET');
      return;
    }

    const isVantageWorkbook = workbookData.workbook.type === SeeqNames.Types.Vantage;
    const workbookLoadNeeded = shouldLoadWorkbook(loadOptions);
    const worksheetLoadData = isVantageWorkbook
      ? await loadVantageWorksheetData(loadOptions, workbookLoadNeeded)
      : loadGraphqlWorksheetData(loadOptions, await normalizeGqlOutput(workbookData), workbookLoadNeeded);
    const { workbook, workbookState, workbookStateOrigin, workstep, shouldSetAddOnTools } = worksheetLoadData;

    setLockedChangeMade(false);

    if (loadOptions.workbookDisplay === WORKBOOK_DISPLAY.EDIT && (!canWriteItem(workbook) || workbook.isArchived)) {
      // This is the only case where the write permission in the response needs to be checked manually so we
      // can redirect to the view-only worksheet view. All other cases will be handled via protected by the use of
      // ProtectedComponent in the Router configuration.
      setWorkbookDisplayMode(WORKBOOK_DISPLAY.VIEW);

      const worksheetUrl = getViewWorksheetLink(
        loadOptions.workbookId,
        loadOptions.worksheetId,
        loadOptions.currentFolderId,
        loadOptions.workstepId,
      );
      goToWithReload(worksheetUrl);

      // Starting a page navigation does not stop execution; without the bail-out the rest of the load runs in
      // edit mode against a worksheet the user cannot write to while the browser navigates away (CRAB-63741).
      return;
    }

    if (!headlessRenderMode()) {
      updateOpenedAt(loadOptions.workbookId);
    }

    await sqStateSynchronizer
      .rehydrate(workbookState, {
        persistenceLevel: 'WORKBOOK',
        initializeMode: 'FORCE',
        origin: workbookStateOrigin,
        workbookId: loadOptions.workbookId,
        worksheetId: loadOptions.worksheetId,
      })
      .then(() => {
        trackOpenedDocument(loadOptions);
      });

    const isSuccessful =
      !isVantageWorkbook && !workstep
        ? await loadWorksheetWithoutWorksteps(loadOptions)
        : await exposedForTesting.loadWorkstep(loadOptions, workstep);
    if (!isSuccessful) {
      return;
    }

    await sqStateSynchronizer.withPushDisabled(async () => {
      if (shouldSetAddOnTools) {
        // The add-on tools were already fetched once for this page load, so they only need to be handed to the
        // investigate store here. This has to happen after the workstep has been rehydrated.
        setAddOnToolsSqInvestigation(loadOptions.toolPaneAddOnTools);
      }

      await fetchAnnotations(loadOptions.workbookId, sqWorkbookStore.isVantage ? null : loadOptions.worksheetId);

      subscribeToSubscriptionChannel(loadOptions);

      if (sqWorkbookStore.isTopic) {
        await loadReportInfo(loadOptions);
      } else {
        await saveDefaultAnnotation(loadOptions);
      }

      const isVantage = sqWorkbookStore.isVantage;
      await (sqWorkbenchStore.journalToOpen
        ? addTrendItem(sqWorkbenchStore.journalToOpen)
        : // If a vantage room, use the workbook ID and make the search exclusive to annotations that are interested in
          // only that workbook id
          displayNewOrExisting(isVantage ? loadOptions.workbookId : loadOptions.worksheetId, isVantage));

      notifyLoading();
      await notifyCapture();
    });
  } catch (error) {
    notifyError(formatMessage`Could not load worksheet: ${error}`);
    if (error === WORKSHEET_WITH_NO_WORKSTEPS) {
      // The worksheet has no worksteps and this mode cannot push one (view mode never pushes), so there is nothing to
      // load. The generic load-error page retries the same URL every few seconds, which would loop forever
      // (CRAB-63893), so use the page that does not retry.
      goTo(getNoWorksheetLink(), true);
    } else {
      navigateToCorrectErrorPage(error, 'ROUTE_NO_WORKBOOK');
    }
  }
}

async function initializeWorksheetWithoutWorksteps(loadOptions: LoadOptions): Promise<WorkstepOutput> {
  // If a worksheet does not have any worksteps then we create an initial empty one. This allows the
  // user to hit the previous button and eventually return to a blank slate rather than their first
  // action.
  sqStateSynchronizer.initialize('WORKSHEET', {
    workbookId: loadOptions.workbookId,
    worksheetId: loadOptions.worksheetId,
  });
  await setCurrentUser();
  const templateWorkstepId = sqWorkbenchStore.currentUser.templateWorkstepId;
  if (!_.isNil(templateWorkstepId)) {
    await loadTemplateSettings(loadOptions.workbookId, loadOptions.worksheetId, templateWorkstepId);
  }

  const pushedWorkstep = sqWorkbookStore.isVantage
    ? await sqStateSynchronizer.pushWorksheetState(loadOptions.workbookId, loadOptions.worksheetId)
    : await sqStateSynchronizer.push(PUSH_WORKSTEP_IMMEDIATE, {
        workbookId: loadOptions.workbookId,
        worksheetId: loadOptions.worksheetId,
      });

  // Note that we don't call rehydrate again because the state is already accurate because of the call to initialize.
  return pushedWorkstep?.current
    ? pushedWorkstep
    : currentWorkstepAction(loadOptions.workbookId, loadOptions.worksheetId);
}

async function loadWorksheetWithoutWorksteps(loadOptions: LoadOptions): Promise<boolean> {
  const workstepAction = await initializeWorksheetWithoutWorksteps(loadOptions);
  const workstepState = workstepAction.current.state;

  sqStateSynchronizer
    .rehydrate(workstepState, {
      beforeFetch: () => beforeFetchUpdateWorkstepState(loadOptions),
      origin: workstepOrigin(workstepAction),
      workbookId: loadOptions.workbookId,
      worksheetId: loadOptions.worksheetId,
    })
    .catch((error) => {
      if (!isHttpAbortError(error)) {
        logError(formatMessage`Error while rehydrating workstep: ${error}`);
      }
    });

  return true;
}

/**
 * Loads the workstep for a worksheet.
 *
 * @returns a `Promise<boolean>` to indicate if the load was successful
 */
async function loadWorkstep(loadOptions: LoadOptions, preloadedWorkstep?: WorkstepOutput): Promise<boolean> {
  // Builder takes care of loading the workstep and in view mode the workstep is ephemeral
  if (getFromBuilder() || _.includes(getCurrentPath(), '/builder')) {
    setFromBuilder(false);

    return true;
  }

  let workstepAction: WorkstepOutput;
  try {
    if (preloadedWorkstep) {
      workstepAction = preloadedWorkstep;
      sqWorkstepsStore.actions.setWorksteps({
        previous: workstepAction.previous,
        current: _.cloneDeep(workstepAction.current),
        next: workstepAction.next,
        last: workstepAction.last,
        isError: false,
      });
    } else {
      workstepAction = loadOptions.workstepId
        ? await getWorkstepAction(loadOptions.workbookId, loadOptions.worksheetId, loadOptions.workstepId)
        : await getWorkstepAction(
            loadOptions.workbookId,
            loadOptions.worksheetId,
            sqWorkbookStore.getWorksheetCurrentWorkstepId(loadOptions.worksheetId),
          );
    }
  } catch (e) {
    if (e !== WORKSHEET_WITH_NO_WORKSTEPS) {
      navigateToCorrectErrorPage(e, 'ROUTE_NO_WORKSHEET');

      return false;
    }

    workstepAction = await initializeWorksheetWithoutWorksteps(loadOptions);
  }

  const workstepState = workstepAction.current.state;
  // The worksheet will show a spinner until the promise returned resolved. Don't return the promise here so
  // that all the data on the chart will instead load asynchronously. The stores' state will be restored
  // synchronously, but data will be fetched from the server in the background
  sqStateSynchronizer
    .rehydrate(workstepState, {
      beforeFetch: () => beforeFetchUpdateWorkstepState(loadOptions),
      origin: workstepOrigin(workstepAction),
      workbookId: loadOptions.workbookId,
      worksheetId: loadOptions.worksheetId,
    })
    .catch((error) => {
      // Errors here would be failure to fetch async state which is not a critical error
      if (!isHttpAbortError(error)) {
        logError(formatMessage`Error while rehydrating workstep: ${error}`);
      }
    });

  return true;
}

async function loadTemplateSettings(workbookId: string, worksheetId: string, templateWorkstepId: string) {
  try {
    await sqStateSynchronizer.loadWorkstepStores(
      templateWorkstepId,
      STORES_REHYDRATED_BY_WORKSTEP_TEMPLATE,
      workbookId,
      worksheetId,
      `Error when parsing workstep data for template workstep:`,
    );
  } catch (error) {
    errorToast({
      messageKey: formatMessage`Error while loading the template worksheet (worksheet preferences): ${error}`,
    });
  }
}

/**
 * Used to overwrite the state of a workstep prior to rehydrating it
 *
 * @param loadOptions - Contains potential fields to overwrite
 */
function beforeFetchUpdateWorkstepState(loadOptions: LoadOptions) {
  const promises = [];
  const displayStart = loadOptions.displayRangeStart;
  const displayEnd = loadOptions.displayRangeEnd;
  const timezone = loadOptions.timezone;
  const summaryType = loadOptions.summaryType;
  const summaryValue = loadOptions.summaryValue;
  const assetId = loadOptions.assetId;
  const scalingTableId = loadOptions.scalingTableId;
  const rowSwapScalingTableId = loadOptions.rowSwapScalingTableId;
  const rowSwapTargetRowItemId = loadOptions.rowSwapTargetRowItemId;
  const hideUncertainty = loadOptions.hideUncertainty;
  const analysisViewType = loadOptions.analysisViewType;

  if (displayStart && displayEnd) {
    displayRange.setParamsInStore(displayStart, displayEnd);
    // Override investigate range as well to avoid it being completely disconnected from the display
    // range
    investigateRange.copyFromDisplayRange();
    // Override auto-update to ensure it doesn't override the hard-coded date range given in the URL, but make sure
    // to still show no
    if (
      sqDurationStore.autoUpdate.mode === AUTO_UPDATE.MODES.AUTO ||
      sqDurationStore.autoUpdate.mode === AUTO_UPDATE.MODES.MANUAL
    ) {
      autoUpdate.setMode(AUTO_UPDATE.MODES.OVERRIDE_VIA_DISPLAY_RANGE_QUERY_PARAMS_TO_SHOW_NOW_CURSOR);
    }
  }

  if (timezone) {
    setTimezone({ name: timezone });
  }

  if (summaryType && summaryValue) {
    const discreteUnits = splitDuration(summaryValue);
    const value = discreteUnits
      ? _.findKey(SummarySliderValues[SummaryTypeEnum.DISCRETE].value, discreteUnits)
      : summaryValue;
    const discrete: FrontendDuration = discreteUnits ?? {
      value: 0,
      units: 'min',
    };
    setTrendSummary(
      {
        type: summaryType,
        value,
        discreteUnits: discrete,
        isSlider: true,
      },
      false,
    );
  }

  if (hideUncertainty) {
    setHideUncertainty(hideUncertainty === 'true');
  }

  if (analysisViewType) {
    setAnalysisViewType(analysisViewType);
    setStatisticsSummaryTabVisible(analysisViewType === SeeqNames.AnalysisViewType.StatisticsSummary);
  }

  if (scalingTableId) {
    promises.push(
      editTableDefinition({ id: scalingTableId }, 'SCALING').then((scalingTable) => {
        if (scalingTable) {
          setAutoFocusTableDefinitionTab(true);
        }
      }),
    );
  }

  // In present/view mode, locked worksheets allow temporary asset swaps driven by the Organizer's asset selection.
  // In edit mode, locked source analyses block asset swaps to preserve their authored state.
  const isEditableWorksheetLoad = loadOptions.workbookDisplay === WORKBOOK_DISPLAY.EDIT;
  if (assetId && (!sqWorkbookStore.isLocked || !isEditableWorksheetLoad)) {
    const itemsPromise = isPresentationWorkbookMode()
      ? Promise.resolve(undefined)
      : Promise.all(getAllItems({}).map((item) => getDependencies({ id: item.id })));
    if (sqWorkbookStore.isVantage) {
      promises.push(
        itemsPromise.then(() => {
          // TODO: CRAB-52112 Filter vantage table view by assetId
        }),
      );
    } else {
      promises.push(
        itemsPromise.then((items) =>
          sqTreesApi
            .getTree({ id: assetId })
            // Make sure we set the correct parent for Treemap
            .then(({ data: tree }) => setParent(_.last(tree.item?.ancestors)).then(() => tree))
            .then((tree) =>
              swapAsset(
                tree.item!,
                (swapPairs) => swapAssets(swapPairs, true),
                items === undefined ? undefined : addAssetsProperty({ items }).items,
              ),
            ),
        ),
      );
    }
  }

  if (rowSwapScalingTableId && rowSwapTargetRowItemId) {
    promises.push(
      swapScalingTableRowFromLoadOptions({ tableId: rowSwapScalingTableId, targetRowItemId: rowSwapTargetRowItemId }),
    );
  }

  if (
    loadOptions.workbookDisplay !== WORKBOOK_DISPLAY.EDIT ||
    sqWorkbookStore.workbookDisplay !== WORKBOOK_DISPLAY.EDIT
  ) {
    resetViewingState();
  }

  return Promise.all(promises);
}

/**
 * Tracks the adding of an item as well as actually adds the item.
 *
 * item - the item to be added.
 * mode - the current search pane mode
 * returns a promise that resolves then the item has been added
 */
export function trackAndAddItem(item: object, mode: SearchModes) {
  doTrack('Worksheets', 'Item added', mode);
  return addTrendItem(item);
}

/**
 * Tracks the loading of a display as well as actually loads the display.
 *
 * display - the display to be loaded.
 * mode - the current search pane mode
 * returns a promise that resolves then the display has been added
 */
export function trackAndLoadDisplay(display: { id: string }, mode: SearchModes): Promise<void> {
  doTrack('Worksheets', 'Display loaded', mode);
  return loadDisplay(display.id);
}
